/*
 * PKI.Chain — order a pile of certificates into a chain and say what is wrong.
 *
 * "Works in Chrome, fails on Android / in curl / in Java" is almost always one
 * bug: the server sends the leaf without the intermediate. Desktop browsers
 * paper over it by caching intermediates from previous sites or fetching them
 * via AIA; most other clients do not. So the interesting output is not
 * "valid/invalid" but the exact file to paste into ssl_certificate.
 *
 * Everything here is offline. We do not decide trust — the root store belongs
 * to the client — we decide STRUCTURE, which is what people get wrong.
 */
(function (root) {
    'use strict';

    var P = (root && root.PKI) || {};
    var Asn1 = P.Asn1 || (typeof require !== 'undefined' ? require('./asn1.js') : null);
    var X509 = P.X509 || (typeof require !== 'undefined' ? require('./x509.js') : null);

    /**
     * @param {Array<Uint8Array>} ders
     * @returns ordered chain + diagnosis
     */
    async function build(ders, opts) {
        opts = opts || {};
        var parsed = [], parseErrors = [];

        ders.forEach(function (der, i) {
            try {
                var c = X509.parse(der);
                c._index = i;
                parsed.push(c);
            } catch (e) {
                parseErrors.push({ index: i, error: e.message });
            }
        });

        // Deduplicate by fingerprint of the DER — pasting the same file twice is
        // common when someone concatenates "cert + fullchain".
        var seen = {}, certs = [], duplicates = 0;
        parsed.forEach(function (c) {
            var k = Asn1.toHex(c.der);
            if (seen[k]) { duplicates++; return; }
            seen[k] = true;
            certs.push(c);
        });

        var issues = [];
        if (parseErrors.length) {
            issues.push({ code: 'UNPARSEABLE', severity: 'error', count: parseErrors.length, details: parseErrors });
        }
        if (duplicates) issues.push({ code: 'DUPLICATE_CERTS', severity: 'info', count: duplicates });
        if (!certs.length) {
            return { ordered: [], leaf: null, root: null, issues: issues, serverChain: [], extras: [], complete: false };
        }

        // ── identify the leaf: a certificate that issues nothing else here.
        var issuesOthers = {};
        certs.forEach(function (a) {
            certs.forEach(function (b) {
                if (a === b) return;
                if (isIssuerOf(a, b)) issuesOthers[a._index] = true;
            });
        });
        var candidates = certs.filter(function (c) { return !issuesOthers[c._index]; });

        var leaf = null;
        if (opts.leafHint) {
            leaf = certs.filter(function (c) { return Asn1.bytesEqual(c.der, opts.leafHint); })[0] || null;
        }
        if (!leaf) {
            // Prefer a non-CA server certificate; fall back to any dead end.
            leaf = candidates.filter(function (c) { return !c.isCa; })[0] ||
                   candidates.filter(function (c) { return c.isServerAuth; })[0] ||
                   candidates[0] || certs[0];
        }
        if (candidates.length > 1) {
            issues.push({ code: 'MULTIPLE_LEAVES', severity: 'warning', count: candidates.length });
        }

        // ── walk up
        var ordered = [leaf], used = {}, cursor = leaf;
        used[leaf._index] = true;
        var guard = 0;
        while (guard++ < 20) {
            if (cursor.selfIssued) break;                       // reached a root
            var issuer = findIssuer(cursor, certs, used);
            if (!issuer) break;
            ordered.push(issuer);
            used[issuer._index] = true;
            cursor = issuer;
        }

        var extras = certs.filter(function (c) { return !used[c._index]; });
        if (extras.length) {
            issues.push({
                code: 'UNRELATED_CERTS', severity: 'warning', count: extras.length,
                subjects: extras.map(function (c) { return c.subject.rfc4514; })
            });
        }

        // ── order of the INPUT (before we fixed it) — the actionable diagnosis
        var inputWasOrdered = true;
        for (var i = 0; i + 1 < certs.length; i++) {
            if (!isIssuerOf(certs[i + 1], certs[i])) { inputWasOrdered = false; break; }
        }
        if (!inputWasOrdered && certs.length > 1) {
            issues.push({ code: 'WRONG_ORDER', severity: 'error' });
        }

        var last = ordered[ordered.length - 1];
        var reachedRoot = !!(last && last.selfIssued);
        var chainComplete = reachedRoot;

        if (!reachedRoot) {
            issues.push({
                code: 'INCOMPLETE_CHAIN', severity: 'error',
                missingIssuer: last ? last.issuer.rfc4514 : null,
                // The AIA caIssuers URL is where the missing intermediate lives.
                fetchUrls: last ? last.caIssuerUrls : []
            });
        }

        // A self-signed root in the served chain is wasted bytes on every
        // handshake and is ignored by clients — worth saying, not an error.
        if (reachedRoot && ordered.length > 1) {
            issues.push({ code: 'ROOT_INCLUDED', severity: 'info' });
        }
        if (ordered.length === 1 && leaf.selfIssued) {
            issues.push({ code: 'SELF_SIGNED_ONLY', severity: 'warning' });
        }

        // ── verify every link
        var links = [];
        for (var j = 0; j + 1 < ordered.length; j++) {
            var childCert = ordered[j], parentCert = ordered[j + 1];
            var v = await X509.verifySignature(childCert, parentCert.spki);
            links.push({
                child: childCert.subject.rfc4514, issuer: parentCert.subject.rfc4514,
                valid: v.valid, reason: v.reason
            });
            if (v.valid === false) {
                issues.push({
                    code: 'BAD_SIGNATURE', severity: 'error',
                    child: childCert.subject.rfc4514, issuer: parentCert.subject.rfc4514
                });
            }
        }
        // A self-signed leaf's own signature is worth checking too.
        if (ordered.length === 1 && leaf.selfIssued) {
            var sv = await X509.verifySignature(leaf, leaf.spki);
            links.push({ child: leaf.subject.rfc4514, issuer: 'self', valid: sv.valid, reason: sv.reason });
        }

        // ── validity window of the chain as a whole: an expired intermediate
        //    breaks the site just as thoroughly as an expired leaf, and is far
        //    harder to spot because the browser blames the leaf.
        ordered.forEach(function (c, idx) {
            if (c.expired) {
                issues.push({
                    code: idx === 0 ? 'LEAF_EXPIRED' : 'INTERMEDIATE_EXPIRED',
                    severity: 'error', subject: c.subject.rfc4514, notAfter: c.notAfter
                });
            }
        });

        return {
            ordered: ordered,
            leaf: leaf,
            root: reachedRoot ? last : null,
            intermediates: ordered.slice(1, reachedRoot ? ordered.length - 1 : ordered.length),
            // What actually belongs in ssl_certificate / SSLCertificateFile:
            // leaf + intermediates, root omitted.
            serverChain: reachedRoot ? ordered.slice(0, ordered.length - 1) : ordered.slice(),
            extras: extras,
            links: links,
            complete: chainComplete,
            reachedRoot: reachedRoot,
            issues: issues
        };
    }

    /**
     * Is `a` the issuer of `b`? Subject/issuer DN equality is the RFC rule; the
     * key identifiers are a stronger check when both are present, and they
     * matter for cross-signed CAs where several certificates share a DN.
     */
    function isIssuerOf(a, b) {
        if (a === b) return false;
        if (!Asn1.bytesEqual(a.subject.der, b.issuer.der)) return false;
        if (b.authorityKeyId && a.subjectKeyId) {
            return b.authorityKeyId === a.subjectKeyId;
        }
        return true;
    }

    function findIssuer(cert, pool, used) {
        var matches = pool.filter(function (c) { return !used[c._index] && isIssuerOf(c, cert); });
        if (!matches.length) return null;
        // Prefer an unexpired issuer — this is how a chain that still contains
        // the retired cross-sign picks the live one.
        var live = matches.filter(function (c) { return !c.expired; });
        return (live[0] || matches[0]);
    }

    /**
     * Produce the PEM file to install, in the order servers expect.
     * nginx/Apache both want leaf first, then intermediates, root omitted.
     */
    function toPem(result) {
        var Pem = P.Pem || root.PKI.Pem;
        return result.serverChain.map(function (c) { return Pem.encode(c.der, 'CERTIFICATE'); }).join('');
    }

    /**
     * Pull every certificate out of whatever a CA served at its AIA URL.
     * They are inconsistent about it: some return raw DER, some a PEM block,
     * and plenty return a PKCS#7 bag (.p7c) holding one certificate — which is
     * the form that makes "I downloaded the intermediate and it does not work"
     * such a common complaint, because a .p7c is not a certificate file.
     */
    function extractCertificates(input) {
        var Pem = P.Pem || root.PKI.Pem;
        var sniffed = Pem.sniff(input);
        var out = [];

        sniffed.blocks.forEach(function (b) {
            if (b.kind === 'certificate') { out.push(b.der); return; }
            if (b.kind === 'pkcs7') { out = out.concat(fromPkcs7(b.der)); }
        });

        // A DER blob that sniffed as neither: try PKCS#7 anyway before giving up.
        if (!out.length && sniffed.blocks.length) {
            out = fromPkcs7(sniffed.blocks[0].der);
        }
        return out;
    }

    function fromPkcs7(der) {
        var out = [];
        try {
            var node = Asn1.parse(der);
            // ContentInfo { contentType, [0] EXPLICIT SignedData }
            var wrapper = Asn1.contextChild(node, 0);
            var signedData = wrapper && wrapper.children ? wrapper.children[0] : null;
            if (!signedData) return out;
            // SignedData { version, digestAlgorithms, encapContentInfo,
            //              [0] IMPLICIT certificates OPTIONAL, ... }
            var certs = Asn1.contextChild(signedData, 0);
            if (!certs || !certs.children) return out;
            certs.children.forEach(function (c) {
                if (c.tag === Asn1.TAG.SEQUENCE) {
                    try { X509.parse(c.full); out.push(c.full); }
                    catch (e) { /* an attribute certificate or CRL — skip it */ }
                }
            });
        } catch (e) { /* not PKCS#7 */ }
        return out;
    }

    var API = { build: build, isIssuerOf: isIssuerOf, toPem: toPem,
                extractCertificates: extractCertificates, fromPkcs7: fromPkcs7 };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Chain = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
